import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Buildings,
  TrendUp,
  ChartLineUp,
  UserFocus,
  ArrowRight,
  CheckCircle,
  Clock,
} from "@phosphor-icons/react";

const ICONS = { Buildings, TrendUp, ChartLineUp, UserFocus };
const CARD_INITIAL = { opacity: 0, y: 20 };
const CARD_ANIMATE = { opacity: 1, y: 0 };

export default function Dashboard() {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [s, sub] = await Promise.all([api.get("/services"), api.get("/submissions")]);
    setServices(s.data);
    setSubmissions(sub.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const subFor = (id) => submissions.find((x) => x.service_id === id);
  const completed = submissions.filter((s) => s.status === "submitted").length;
  const active = submissions.filter((s) => s.status === "in_progress").length;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-10">
          <div className="overline text-primary mb-2">Your dashboard</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter">
            Let's grow your business.
          </h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
          {[
            ["Services enrolled", submissions.length, TrendUp],
            ["In progress", active, Clock],
            ["Submitted", completed, CheckCircle],
          ].map(([label, val, Icon]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-zinc-900/50 p-6" data-testid={`stat-${label}`}>
              <Icon size={22} weight="duotone" className="text-primary mb-3" />
              <div className="font-display text-3xl tracking-tighter">{val}</div>
              <div className="text-sm text-muted-foreground mt-1">{label}</div>
            </div>
          ))}
        </div>

        <h2 className="font-display text-xl tracking-tight mb-2">Our services</h2>
        <p className="text-sm text-muted-foreground mb-5">
          Fill out the step-by-step form for any service — you'll choose payment options at the end.
        </p>
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {loading
            ? [1, 2, 3, 4].map((i) => (
                <div key={i} className="h-80 rounded-xl border border-white/10 bg-zinc-900/40 animate-pulse" />
              ))
            : services.map((s, i) => {
                const Icon = ICONS[s.icon] || Buildings;
                const sub = subFor(s.id);
                const total = s.steps.length;
                const stepDone = sub ? Math.min(sub.current_step, total) : 0;
                const pct = sub ? (sub.status === "submitted" ? 100 : Math.round((stepDone / total) * 100)) : 0;

                return (
                  <motion.div
                    key={s.id}
                    initial={CARD_INITIAL}
                    animate={CARD_ANIMATE}
                    transition={{ delay: i * 0.08 }}
                    className="flex flex-col rounded-xl border border-white/10 bg-zinc-900/50 p-7 hover:border-primary/40 transition-colors"
                    data-testid={`dash-service-${s.id}`}
                  >
                    <div className="flex items-start justify-between mb-5">
                      <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon size={24} weight="duotone" className="text-primary" />
                      </div>
                      <div className="text-right">
                        <div className="font-display text-lg tracking-tighter text-primary" data-testid={`price-${s.id}`}>
                          {s.price_label ? s.price_label : (s.price ? `$${s.price.toLocaleString()}` : "Custom")}
                        </div>
                        {sub?.status === "submitted" && <div className="text-[11px] text-green-400">Submitted</div>}
                      </div>
                    </div>
                    <h3 className="font-display text-lg tracking-tight mb-2">{s.name}</h3>
                    <p className="text-sm text-muted-foreground mb-6 flex-1">{s.tagline}</p>

                    {sub && (
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-muted-foreground mb-2">
                          <span>{sub.status === "submitted" ? "Completed" : `Step ${stepDone} of ${total}`}</span>
                          <span>{pct}%</span>
                        </div>
                        <Progress value={pct} className="h-1.5" />
                      </div>
                    )}

                    <Button
                      onClick={() => navigate(`/service/${s.id}`)}
                      data-testid={`start-service-${s.id}`}
                      className={
                        sub?.status === "submitted"
                          ? "w-full bg-secondary text-foreground hover:bg-secondary/80"
                          : "w-full bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform"
                      }
                    >
                      {sub ? (sub.status === "submitted" ? "View submission" : "Continue") : "Start now"}
                      <ArrowRight size={16} className="ml-2" />
                    </Button>
                  </motion.div>
                );
              })}
        </div>
      </main>
    </div>
  );
}
